// @flow

import * as React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import BrandIcon from 'sail/BrandIcon';
import * as BrandIcons from 'sail/BrandIcon/Assets';
import {ProductIcon} from 'sail/ProductIcon';
import {allAdminRolesProps} from './AdminRoleDescriptions';
import {allConnectRoleProps} from './ConnectRoleDescriptions';
import {allCorporateCardRoleProps} from './CorporateCardRoleDescriptions';
import {allDeveloperRoleProps} from './DeveloperRoleDescriptions';
import {allOpalRoleProps} from './OpalRoleDescriptions';
import {allPaymentRoleProps} from './PaymentRoleDescriptions';
import {RoleDescriptionsContainer} from './RoleDescription';
import {allSupportRoleProps} from './SupportRoleDescriptions';
import {allTaxRoleProps} from './TaxRoleDescriptions';
import {allViewOnlyRoleProps} from './ViewOnlyRoleDescriptions';

const groupNames = defineMessages({
  admin: {
    id: 'docsApp.components.contrib.groupNames.admin',
    description: 'The name of the admin group of roles',
    defaultMessage: 'Admin roles',
  },
  connect: {
    id: 'docsApp.components.contrib.groupNames.connect',
    description: 'The name of the connect group of roles',
    defaultMessage: 'Connect roles',
  },
  corporateCard: {
    id: 'docsApp.components.contrib.groupNames.corporateCard',
    description: 'The name of the Corporate Card group of roles',
    defaultMessage: 'Corporate card roles',
  },
  developer: {
    id: 'docsApp.components.contrib.groupNames.developer',
    description: 'The name of the developer group of roles',
    defaultMessage: 'Developer roles',
  },
  payment: {
    id: 'docsApp.components.contrib.groupNames.payment',
    description: 'The name of the payment group of roles',
    defaultMessage: 'Payment roles',
  },
  opal: {
    id: 'docsApp.components.contrib.groupNames.opal',
    description: 'The name of the Opal group of roles',
    defaultMessage: 'Opal roles',
  },
  support: {
    id: 'docsApp.components.contrib.groupNames.support',
    description: 'The name of the support group of roles',
    defaultMessage: 'Support roles',
  },
  tax: {
    id: 'docsApp.components.contrib.groupNames.tax',
    description: 'The name of the tax group of roles',
    defaultMessage: 'Tax form roles',
  },
  viewOnly: {
    id: 'docsApp.components.contrib.groupNames.viewOnly',
    description: 'The name of the view only group of roles',
    defaultMessage: 'View only roles',
  },
});

const groupDescriptions = defineMessages({
  connect: {
    id: 'docsApp.components.contrib.groupDescriptions.connect',
    description: 'A message explaining that these roles are for connect.',
    defaultMessage: 'These roles are only available if you use <a>Connect</a>',
  },
  corporateCard: {
    id: 'docsApp.components.contrib.groupDescriptions.corporateCard',
    description:
      'A message explaining that these roles are for corporate card.',
    defaultMessage:
      'These roles are only available if you use <a>Corporate Card</a>',
  },
  tax: {
    id: 'docsApp.components.contrib.groupDescriptions.tax',
    description: 'A message explaining that these roles are for tax.',
    defaultMessage: 'These roles are only available if you use <a>1099s</a>',
  },
});

const groupNotes = defineMessages({
  admin: {
    id: 'docsApp.components.contrib.groupNotes.admin',
    description: 'A message explaining more context about admin roles.',
    defaultMessage: `An Account Owner is a special type of Administrator that can perform all actions, including closing the account.
      There can only be one Owner for an account. To change the Account Owner, please refer to <a>this guide</a>.`,
  },
});

const GroupDescriptionMap = {
  admin: (
    <RoleDescriptionsContainer
      roleDescriptions={allAdminRolesProps}
      groupName={<FormattedMessage {...groupNames.admin} />}
      groupIcon={<BrandIcon icon={BrandIcons.stripe} size={20} />}
      groupFootnote={
        <FormattedMessage
          {...groupNotes.admin}
          values={{
            a: (content) => (
              <a href="https://support.stripe.com/questions/change-the-owner-of-a-stripe-account">
                {content}
              </a>
            ),
          }}
        />
      }
    />
  ),
  connect: (
    <RoleDescriptionsContainer
      roleDescriptions={allConnectRoleProps}
      groupName={<FormattedMessage {...groupNames.connect} />}
      groupIcon={<ProductIcon icon={'connect'} size={20} />}
      groupDescription={
        <FormattedMessage
          {...groupDescriptions.connect}
          values={{
            a: (content) => (
              <a href="https://stripe.com/docs/connect">{content}</a>
            ),
          }}
        />
      }
    />
  ),
  corporateCard: (
    <RoleDescriptionsContainer
      roleDescriptions={allCorporateCardRoleProps}
      groupName={<FormattedMessage {...groupNames.corporateCard} />}
      groupIcon={<ProductIcon icon={'corporatecard'} size={20} />}
      groupDescription={
        <FormattedMessage
          {...groupDescriptions.corporateCard}
          values={{
            a: (content) => (
              <a href="https://stripe.com/docs/corporate-card">{content}</a>
            ),
          }}
        />
      }
    />
  ),
  developer: (
    <RoleDescriptionsContainer
      roleDescriptions={allDeveloperRoleProps}
      groupName={<FormattedMessage {...groupNames.developer} />}
      groupIcon={<BrandIcon icon={BrandIcons.stripe} size={20} />}
    />
  ),
  opal: (
    <RoleDescriptionsContainer
      roleDescriptions={allOpalRoleProps}
      groupName={<FormattedMessage {...groupNames.opal} />}
      groupIcon={<BrandIcon icon={BrandIcons.stripe} size={20} />}
    />
  ),
  payment: (
    <RoleDescriptionsContainer
      roleDescriptions={allPaymentRoleProps}
      groupName={<FormattedMessage {...groupNames.payment} />}
      groupIcon={<ProductIcon icon={'payments'} size={20} />}
    />
  ),
  support: (
    <RoleDescriptionsContainer
      roleDescriptions={allSupportRoleProps}
      groupName={<FormattedMessage {...groupNames.support} />}
      groupIcon={<BrandIcon icon={BrandIcons.stripe} size={20} />}
    />
  ),
  tax: (
    <RoleDescriptionsContainer
      roleDescriptions={allTaxRoleProps}
      groupName={<FormattedMessage {...groupNames.tax} />}
      groupIcon={<ProductIcon icon={'connect'} size={20} />}
      groupDescription={
        <FormattedMessage
          {...groupDescriptions.tax}
          values={{
            a: (content) => (
              <a href="https://stripe.com/docs/connect/tax-reporting">
                {content}
              </a>
            ),
          }}
        />
      }
    />
  ),
  viewOnly: (
    <RoleDescriptionsContainer
      roleDescriptions={allViewOnlyRoleProps}
      groupName={<FormattedMessage {...groupNames.viewOnly} />}
      groupIcon={<BrandIcon icon={BrandIcons.stripe} size={20} />}
    />
  ),
};

type RoleGroupDescriptionProps = {|
  groupName: string,
|};

export const RoleGroupDescription = ({
  groupName,
}: RoleGroupDescriptionProps) => {
  return GroupDescriptionMap[groupName];
};
